import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { stepLabel } from '../utils/activitySteps';
import { useTypewriter } from '../utils/useTypewriter';
import { CheckCircle2, ChevronDown } from 'lucide-react';

/**
 * The model's activity for one turn: what it thought, which tools it called,
 * how they went.
 *
 * Two states, deliberately different. While the turn runs the trail is open and
 * grows step by step — watching the agent work is the point, and the header
 * names what is happening right now ("Running Search..."). Once it finishes the
 * whole thing folds into one summary line ("Worked for 3.5s · 4 steps"), because
 * the answer is the point and the trail is there for when it is doubted. A tool
 * that failed is NOT called out on that summary line: the model routinely
 * recovers from a failed call and still answers, so a red badge over a good
 * answer reads as "this answer is broken" when it isn't. Failures are shown
 * per-step inside the expanded trail, where they have the context to mean
 * something.
 *
 * Steps arrive in the shape canvas_service persists and streams:
 * `{type: 'thought'|'tool'|'status', content, tool, status, ts}`.
 */

// Only allow safe link protocols in activity markdown.
const SAFE_HREF_RE = /^(https?:|mailto:|tel:|\/|#)/i;
const sanitizeHref = (href) => (href && SAFE_HREF_RE.test(href) ? href : '#');

// Steps carry ts as epoch-seconds float. Render HH:MM:SS local, or "" if absent.
const fmtTs = (ts) => {
  if (typeof ts !== 'number' || !isFinite(ts)) return '';
  const dt = new Date(ts * 1000);
  if (isNaN(dt.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(dt.getHours())}:${p(dt.getMinutes())}:${p(dt.getSeconds())}`;
};

/**
 * Wall-clock span of the turn, from the first step's ts to the last.
 * Null when fewer than two steps carry a usable ts — better to show nothing
 * than to claim "0.0s" for a turn whose timing we don't actually know.
 */
const spanSeconds = (steps) => {
  const stamps = steps
    .map((s) => s.ts)
    .filter((ts) => typeof ts === 'number' && isFinite(ts));
  if (stamps.length < 2) return null;
  const span = Math.max(...stamps) - Math.min(...stamps);
  return span > 0 ? span : null;
};

const fmtDuration = (secs) => {
  if (secs == null) return '';
  if (secs < 60) return `${secs.toFixed(1)}s`;
  const mins = Math.floor(secs / 60);
  const rem = Math.round(secs % 60);
  return rem ? `${mins}m ${rem}s` : `${mins}m`;
};

/**
 * Tool arguments as [label, value] pairs ready to render.
 *
 * `_clipped` is the marker the backend uses when a payload was too large to
 * carry whole (`react_agent._trace_args`). It is a transport detail, not an
 * argument name, so it renders as a bare line rather than a key called
 * "_clipped".
 */
const argEntries = (args) => {
  if (!args || typeof args !== 'object' || Array.isArray(args)) return [];
  return Object.entries(args).map(([k, v]) => {
    const value = v !== null && typeof v === 'object' ? JSON.stringify(v) : String(v);
    return k === '_clipped' ? ['', value] : [k, value];
  });
};

/** One-line `key: value · key: value` gist of the arguments. */
const argSummary = (args) => {
  const parts = argEntries(args).map(([k, v]) => (k ? `${k}: ${v}` : v));
  if (!parts.length) return '';
  const joined = parts.join(' · ');
  return joined.length > 120 ? `${joined.slice(0, 119)}…` : joined;
};

/** The supporting detail under a live step: what the tool was called with. */
const stepDetail = (step) => {
  if (!step || step.type !== 'tool') return '';
  const fromArgs = argSummary(step.args);
  if (fromArgs) return fromArgs;
  const content = typeof step.content === 'string' ? step.content : '';
  // Fall back to the head of the content for producers that send no args.
  return content.split('\n\n')[0].split('\n').slice(0, 2).join(' ').trim();
};

/**
 * What the header says while the turn is still running. Deliberately about the
 * activity rather than the step's own text — the step's text is on its row, one
 * line below, and repeating it there reads as a stutter.
 */
const liveLabel = (step, isFirst) => {
  if (!step) return 'Working...';
  if (step.type === 'tool') return `Running ${step.tool || 'tool'}...`;
  if (step.type === 'status') {
    // The turn's first status step is the agent's entry ping — its own name.
    // As a header that reads as idle branding rather than activity, so say
    // what is happening instead. The row below still shows the name.
    if (isFirst) return 'Thinking it through...';
    return stepLabel(step) || 'Working...';
  }
  return 'Thinking it through...';
};

/** 'failed' | 'skipped' | 'ok' | '' — a tool's outcome, '' while it is still in flight. */
const toolOutcome = (step) => {
  const status = typeof step?.status === 'string' ? step.status : '';
  if (status.startsWith('failed')) return 'failed';
  if (status.startsWith('skipped')) return 'skipped';
  return status === 'ok' ? 'ok' : '';
};

// The spine is drawn per row rather than once behind the list, so it can be
// clipped to start at the first node and stop at the last. Every row therefore
// has to agree on the geometry: SPINE_LEFT is the line's left edge, NODE_MID is
// the node's centre measured from the row's top (py-1.5 + half a leading-5 line).
const SPINE_LEFT = 10;
const SPINE_WIDTH = 1.5;
const NODE_SIZE = 10;
const NODE_TOP = 11;
const NODE_MID = NODE_TOP + NODE_SIZE / 2;
const NODE_LEFT = SPINE_LEFT - (NODE_SIZE - SPINE_WIDTH) / 2;

// Size is left to the caller so the header and the rows can differ without
// relying on which width utility Tailwind happens to emit last.
const CHEVRON = 'shrink-0 text-fg-muted transition-transform duration-[var(--d-base)] ease-[var(--ease-out)]';
// The focus ring is inset because the shell clips overflow, and an outset ring
// (or the UA outline) on the header would be painted away by that clip.
const FOCUS_RING = 'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand';
const ROW_BUTTON = `w-full flex items-center gap-3 py-1.5 pr-1 text-left rounded-md transition-colors duration-[var(--d-fast)] hover:bg-surface-sunken ${FOCUS_RING}`;
const HEADER_BUTTON = `w-full flex items-center gap-2 px-3 py-2 text-left transition-colors duration-[var(--d-fast)] hover:bg-surface-sunken ${FOCUS_RING}`;
const TS_TEXT = 'shrink-0 text-[11px] font-mono tabular-nums text-fg-secondary';

// Self-contained markdown for a step body. Not ChatMessage's MarkdownRenderer
// (unexported + would create a circular import).
const StepMarkdown = ({ content }) => (
  <div className="text-sm leading-relaxed break-words text-fg-secondary">
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ href, children, ...p }) => (
          <a {...p} href={sanitizeHref(href)} target="_blank" rel="noopener noreferrer"
             className="text-brand underline">{children}</a>
        ),
        code: ({ children, ...p }) => (
          <code {...p} className="px-1 py-0.5 rounded text-xs font-mono bg-surface-sunken text-fg">{children}</code>
        ),
        p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
        ul: ({ children }) => <ul className="list-disc ml-4 mb-1">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal ml-4 mb-1">{children}</ol>,
      }}
    >
      {typeof content === 'string' ? content : String(content ?? '')}
    </ReactMarkdown>
  </div>
);
StepMarkdown.propTypes = { content: PropTypes.any };

const ActivityTimeline = ({ steps = [], isStreaming = false, isDarkMode = false }) => {
  // Open while the turn runs, so the trail grows in front of the reader.
  const [open, setOpen] = useState(isStreaming);
  // A Set, not a single index: comparing two tool calls is the main reason to
  // open the trail at all, and one-at-a-time made that impossible.
  const [expanded, setExpanded] = useState(() => new Set());

  // A turn that just finished should not stay expanded from its streaming
  // state — the answer takes over and the trail steps back.
  useEffect(() => { if (!isStreaming) setOpen(false); }, [isStreaming]);

  const lastStep = Array.isArray(steps) && steps.length ? steps[steps.length - 1] : null;
  // Hooks cannot sit behind the early returns below, so the label is computed
  // here for every render and simply unused when the panel is collapsed or the
  // turn has finished.
  const { text: typed, animating: typing } = useTypewriter(
    isStreaming ? liveLabel(lastStep, Array.isArray(steps) && steps.length === 1) : '',
    // replayMs: with no new step to change the label, retype it every few
    // seconds so a long silent phase (the planner, a slow tool) reads as alive.
    { enabled: isStreaming, replayMs: 3000 },
  );

  if (!Array.isArray(steps) || steps.length === 0) return null;

  const lastIdx = steps.length - 1;

  const toggleStep = (i) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });

  const nodeTone = (step, i) => {
    // skipped before the streaming guard: a guardrail short-circuit that is the
    // current step should not animate as active work in progress
    if (step.type === 'tool' && toolOutcome(step) === 'skipped') return 'border-line-strong bg-surface-sunken';
    if (isStreaming && i === lastIdx) return 'bg-brand border-brand node-pulse';
    if (step.type === 'thought') return 'border-brand bg-surface';
    if (step.type === 'tool') {
      const outcome = toolOutcome(step);
      if (outcome === 'failed') return 'border-danger bg-danger-soft';
      if (outcome === 'ok') return 'border-success bg-success-soft';
    }
    return 'border-line-strong bg-surface';
  };

  // A lone step connects to nothing, so it draws no line at all.
  const spine = (i) => (steps.length < 2 ? null : (
    <span aria-hidden="true" className="absolute bg-line-strong"
      style={{
        left: SPINE_LEFT,
        width: SPINE_WIDTH,
        top: i === 0 ? NODE_MID : 0,
        bottom: i === lastIdx ? `calc(100% - ${NODE_MID}px)` : 0,
      }} />
  ));

  const node = (step, i) => (
    <span aria-hidden="true"
      className={`absolute rounded-full border-2 ${nodeTone(step, i)}`}
      style={{ left: NODE_LEFT, top: NODE_TOP, width: NODE_SIZE, height: NODE_SIZE }} />
  );

  /**
   * The expandable body of a row. Mounted only while open: a thought's first
   * line is already its row label, so keeping the full body in the DOM would
   * put the same words on screen twice for the reader and twice again for a
   * screen reader. The collapse-grid wrapper stays put so opening still eases.
   */
  const panel = (isOpen, children) => (
    <div className={`collapse-grid${isOpen ? ' collapse-grid-open' : ''}`}>
      <div>
        {isOpen && (
          <div className="mt-1 mb-1.5 space-y-2 rounded-lg border border-line bg-surface-sunken px-3 py-2">
            {children}
          </div>
        )}
      </div>
    </div>
  );

  const renderRow = (step, i) => {
    const ts = fmtTs(step.ts);
    const isOpen = expanded.has(i);
    const frame = (children) => (
      <div key={i} className="relative pl-7 animate-rise">
        {spine(i)}
        {node(step, i)}
        {children}
      </div>
    );

    // ----- status: muted single-line label, nothing to expand -----
    if (step.type === 'status') {
      return frame(
        <div className="flex items-center gap-3 py-1.5 pr-1">
          <span className="min-w-0 flex-1 text-[13px] leading-5 text-fg-secondary break-words">{step.content}</span>
          {ts && <span className={TS_TEXT}>{ts}</span>}
        </div>
      );
    }

    // ----- tool: name, what it was called with, outcome, expandable body -----
    if (step.type === 'tool') {
      const outcome = toolOutcome(step);
      const errTail = outcome === 'failed' ? step.status.replace(/^failed:?\s*/, '') : '';
      const args = argEntries(step.args);
      const detail = stepDetail(step);
      return frame(
        <>
          <button type="button" aria-expanded={isOpen} onClick={() => toggleStep(i)} className={ROW_BUTTON}>
            <span className="shrink-0 text-[13px] leading-5 font-medium text-fg">{step.tool}</span>
            {detail && (
              <span className="min-w-0 flex-1 truncate text-[11px] leading-5 font-mono tabular-nums text-fg-secondary">
                {detail}
              </span>
            )}
            {outcome && (
              <span className={`shrink-0 text-[10px] font-semibold rounded-full px-2 py-0.5 ${
                outcome === 'failed' ? 'bg-danger-soft text-danger'
                  : outcome === 'skipped' ? 'bg-surface-sunken text-fg-muted'
                  : 'bg-success-soft text-success'}`}>
                {outcome}
              </span>
            )}
            {ts && <span className={TS_TEXT}>{ts}</span>}
            <ChevronDown aria-hidden="true" className={`${CHEVRON} w-3.5 h-3.5 ml-auto ${isOpen ? '' : '-rotate-90'}`} />
          </button>
          {panel(isOpen, (
            <>
              {errTail && <p className="text-[13px] text-danger break-words">{errTail}</p>}
              {args.length > 0 && (
                <div>
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-fg-secondary">Parameters</p>
                  <dl className="space-y-0.5">
                    {args.map(([k, v], ai) => (
                      <div key={ai} className="flex gap-2 text-[11px] font-mono">
                        {k && <dt className="shrink-0 text-fg-secondary">{k}</dt>}
                        <dd className="min-w-0 break-all text-fg-secondary">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
              {step.content && <StepMarkdown content={step.content} />}
            </>
          ))}
        </>
      );
    }

    // ----- thought (default): first line on the row, full markdown on expand -----
    return frame(
      <>
        <button type="button" aria-expanded={isOpen} onClick={() => toggleStep(i)} className={ROW_BUTTON}>
          <span className="min-w-0 flex-1 truncate text-[13px] leading-5 text-fg-secondary">{stepLabel(step)}</span>
          {ts && <span className={TS_TEXT}>{ts}</span>}
          <ChevronDown aria-hidden="true" className={`${CHEVRON} w-3.5 h-3.5 ml-auto ${isOpen ? '' : '-rotate-90'}`} />
        </button>
        {panel(isOpen, <StepMarkdown content={step.content} />)}
      </>
    );
  };

  // Rows sit flush against each other: each one draws its own segment of the
  // spine from edge to edge, so any vertical gap would break the line.
  const rows = <div className="border-t border-line px-2 py-2">{steps.map(renderRow)}</div>;

  // `bg-surface-raised` rather than the /50 the design called for: Tailwind
  // cannot apply an alpha modifier to a var()-backed colour, so `/50` would
  // silently generate no background at all.
  const shell = 'mb-2 rounded-xl border border-line bg-surface-raised overflow-hidden';

  // ---------- live: a summary line that keeps changing, trail open beneath ----------
  if (isStreaming) {
    return (
      <div className={shell}>
        <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)} className={HEADER_BUTTON}>
          <span aria-hidden="true" className="orb-spin grid h-3.5 w-3.5 shrink-0 place-items-center rounded-full">
            <span className="h-2 w-2 rounded-full bg-surface-raised" />
          </span>
          {/* Typed in, and backspaced out when the step changes: swapping the
              string outright reads as a flicker — you see that something moved
              but not what. No remount key here, because the typing IS the
              transition now; remounting would restart it mid-word.

              shimmer-text carries the same sweep the waiting indicator used, so
              the line reads as in-flight rather than as a finished label. This
              panel is the only live surface once that indicator steps aside, and
              without motion a stalled turn looks identical to a completed one.
              It sets a text colour, so text-fg would be overridden anyway. */}
          <span className="shimmer-text min-w-0 flex-1 truncate text-[13px] font-medium">
            {typed}
            {/* Only while characters are moving — a caret left blinking on a
                settled label would claim the turn is still writing. */}
            {typing && (
              <span aria-hidden="true" className="caret-blink ml-px inline-block w-[1px] align-middle">
                &nbsp;
              </span>
            )}
          </span>
          <ChevronDown aria-hidden="true" className={`${CHEVRON} w-4 h-4 ${open ? '' : '-rotate-90'}`} />
        </button>
        {open && rows}
      </div>
    );
  }

  // ---------- finished: one summary line, expandable ----------
  const duration = fmtDuration(spanSeconds(steps));
  const countLabel = `${steps.length} step${steps.length === 1 ? '' : 's'}`;
  return (
    <div className={shell}>
      <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)} className={HEADER_BUTTON}>
        <CheckCircle2 aria-hidden="true" className="w-4 h-4 shrink-0 text-success" />
        <span className="text-[13px] font-medium text-fg-secondary">
          {duration ? `Worked for ${duration} · ${countLabel}` : countLabel}
        </span>
        <ChevronDown aria-hidden="true" className={`${CHEVRON} w-4 h-4 ml-auto ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && rows}
    </div>
  );
};

ActivityTimeline.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.oneOf(['thought', 'tool', 'status']),
    content: PropTypes.string,
    tool: PropTypes.string,
    status: PropTypes.string,
    args: PropTypes.object,
    ts: PropTypes.number,
  })),
  isStreaming: PropTypes.bool,
  // Still accepted because the parent passes it, but the timeline is driven by
  // theme tokens now and renders the same in all eight themes.
  isDarkMode: PropTypes.bool,
};

export default ActivityTimeline;
